import { api } from '@ppwr/convex/_generated/api'
import type { LwCourseProgressSnapshot } from '@ppwr/convex/learnworlds/types'
import { useAction } from 'convex/react'
import { useCallback, useEffect, useRef } from 'react'

const CLOSED_POLL_MS = 1000

type OpenSession = {
  tab: Window
  slug: string
  baseline: { lwUserId: string; progress: LwCourseProgressSnapshot | null } | null
  timer: ReturnType<typeof setInterval>
}

/**
 * Web counterpart of `CoursePlayerScreen`: opens the course straight on LearnWorlds in a
 * new tab instead of embedding it, since the player does not work inside an iframe.
 *
 * The tab is opened synchronously in the click handler and pointed at the SSO url once
 * it is minted — opening it after the `await` would lose the user gesture and get
 * caught by the popup blocker. Progress is diffed when the tab is closed, mirroring the
 * native player's unmount.
 */
export const useWebCoursePlayer = (onError: (message: string) => void) => {
  const getPlayerUrl = useAction(api.learnworlds.actions.getPlayerUrl)
  const trackCourseProgress = useAction(api.learnworlds.actions.trackCourseProgress)
  const session = useRef<OpenSession | null>(null)

  const finish = useCallback(() => {
    const current = session.current
    if (!current) return
    session.current = null
    clearInterval(current.timer)
    if (!current.baseline) return
    const { lwUserId, progress } = current.baseline
    trackCourseProgress({ slug: current.slug, lwUserId, before: progress }).catch((e: Error) =>
      console.warn('[courses] trackCourseProgress failed', e.message)
    )
  }, [trackCourseProgress])

  useEffect(() => finish, [finish])

  return useCallback(
    (slug: string, unitId?: string) => {
      finish()
      const tab = window.open('', '_blank')
      if (!tab) {
        onError('Your browser blocked the course window. Allow pop-ups for this site and try again.')
        return
      }

      const current: OpenSession = {
        tab,
        slug,
        baseline: null,
        timer: setInterval(() => tab.closed && finish(), CLOSED_POLL_MS)
      }
      session.current = current

      getPlayerUrl({ slug, unitId })
        .then(({ url, lwUserId, progress }) => {
          if (lwUserId) current.baseline = { lwUserId, progress }
          tab.location.href = url
        })
        .catch((e: Error) => {
          tab.close()
          onError(e.message)
        })
    },
    [finish, getPlayerUrl, onError]
  )
}
